import { currencyKey } from '@nxt-insurance/constants'
import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import type { FormattingLocale } from './types'

interface Dependencies {
  getCurrentLanguage: () => Language
  getCurrentMarket: () => SupportedMarketKey
}

export class IntlFormatter {
  private dependencies: Dependencies
  constructor(dependencies: Dependencies) {
    this.dependencies = dependencies
  }

  private getDependency = <TDependencyKey extends keyof Dependencies>(
    key: TDependencyKey
  ): Dependencies[TDependencyKey] => {
    return this.dependencies[key]
  }

  getLocaleString = (
    marketKey: SupportedMarketKey = this.getDependency('getCurrentMarket')(),
    language: Language = this.getDependency('getCurrentLanguage')()
  ): FormattingLocale => `${language}-${marketKey}` // The BCP 47 (https://tools.ietf.org/html/rfc5646#section-2.1.1) language tag is case insensitive

  getEnforcedFormattingLocale = (): FormattingLocale => {
    const localeString = this.getLocaleString()
    const isEnglishLanguage = localeString.startsWith('en-')
    if (isEnglishLanguage) {
      return 'en-de'
    }
    return localeString
  }

  private getNumberFormatter = (options?: Omit<Intl.NumberFormatOptions, 'style'>) => {
    const localeString = this.getEnforcedFormattingLocale()
    return new Intl.NumberFormat(localeString, {
      style: 'decimal',
      ...options,
    })
  }

  private getCurrencyFormatter = (options?: Omit<Intl.NumberFormatOptions, 'style'>) => {
    const localeString = this.getEnforcedFormattingLocale()

    const market = this.getDependency('getCurrentMarket')()

    return new Intl.NumberFormat(localeString, {
      currencyDisplay: 'symbol',
      style: 'currency',
      currency: currencyKey[market],
      minimumFractionDigits: 0,
      ...options,
    })
  }

  private getCurrencySymbol = (options?: Omit<Intl.NumberFormatOptions, 'style'>): string => {
    const market = this.getDependency('getCurrentMarket')()
    const formatter = new Intl.NumberFormat('de', {
      currencyDisplay: 'symbol',
      style: 'currency',
      currency: currencyKey[market],
      minimumFractionDigits: 0,
      ...options,
    })
    return formatter.format(0).replaceAll(/[\d\s,.]/g, '')
  }

  private getCurrencyFractionDigits = (currency: string): number => {
    try {
      return (
        new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
          .maximumFractionDigits ?? 2
      )
    } catch {
      return 2
    }
  }

  private getPercentFormatter = (options?: Omit<Intl.NumberFormatOptions, 'style'>) => {
    const localeString = this.getEnforcedFormattingLocale()
    return new Intl.NumberFormat(localeString, {
      style: 'percent',
      ...options,
    })
  }

  getNumberSeparators = (): {
    thousandSeparator: string
    decimalSeparator: string
  } => {
    const formatterFn = this.getNumberFormatter()
    const thousandSeparator = formatterFn.format(1111).replaceAll('1', '')
    const decimalSeparator = formatterFn.format(1.1).replaceAll('1', '')
    return { thousandSeparator, decimalSeparator }
  }

  formatNumber = (
    value: number | bigint,
    options?: Omit<Intl.NumberFormatOptions, 'style'>
  ): string => {
    const numberFormatter = this.getNumberFormatter(options)
    return numberFormatter.format(value)
  }

  formatCurrency = (
    value: bigint | number | string,
    options?: Omit<Intl.NumberFormatOptions, 'style'>
  ): string => {
    const amount: bigint | number =
      typeof value === 'string' ? Number(value.replace(',', '.')) : value

    const isEnglishLanguage = this.getLocaleString().startsWith('en-')
    if (!isEnglishLanguage) {
      return this.getCurrencyFormatter(options).format(amount)
    }

    const market = this.getDependency('getCurrentMarket')()
    const symbol = this.getCurrencySymbol(options)
    const numberFormatter = this.getNumberFormatter({
      minimumFractionDigits: 0,
      maximumFractionDigits: this.getCurrencyFractionDigits(currencyKey[market]),
      ...options,
    })
    return symbol + numberFormatter.format(amount)
  }

  formatPrice = (
    value: number | string,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'minimumFractionDigits'>
  ): string => {
    const priceOptions = {
      ...options,
      minimumFractionDigits: 2,
    }
    return this.formatCurrency(value, priceOptions)
  }

  formatPercent = (
    value: number | string,
    options?: Omit<Intl.NumberFormatOptions, 'style'>
  ): string => {
    const amount = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value)
    const percentFormatter = this.getPercentFormatter(options)
    return percentFormatter.format(amount / 100)
  }

  parseFormattedNumber = (stringNumber: string): number => {
    const { thousandSeparator, decimalSeparator } = this.getNumberSeparators()

    return Number.parseFloat(
      stringNumber
        .replaceAll(new RegExp(`\\${thousandSeparator}`, 'g'), '')
        .replace(new RegExp(`\\${decimalSeparator}`), '.')
    )
  }

  formatToLocaleDate = (dateString: string): string => {
    const date = new Date(dateString)
    const locale = this.getEnforcedFormattingLocale()
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }
    return date.toLocaleDateString(locale, options)
  }
}
